import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  location?: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  appointmentType?: string;
  status?: 'scheduled' | 'complete' | 'no-show' | 'cancelled' | 'on-the-way' | 'in-progress';
  color?: string;
  assignedTo?: string;
}

interface CalendarContextType {
  calendarEvents: CalendarEvent[];
  addCalendarEvent: (event: Omit<CalendarEvent, 'id'>) => string;
  updateCalendarEvent: (id: string, event: Partial<CalendarEvent>) => void;
  removeCalendarEvent: (id: string) => void;
  getCalendarEvent: (id: string) => CalendarEvent | undefined;
}

const CalendarContext = createContext<CalendarContextType | undefined>(undefined);

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
};

interface CalendarProviderProps {
  children: ReactNode;
}

export const CalendarProvider: React.FC<CalendarProviderProps> = ({ children }) => {
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);

  const addCalendarEvent = (event: Omit<CalendarEvent, 'id'>) => {
    const id = Date.now().toString();
    const newEvent = { ...event, id };
    setCalendarEvents(prev => [...prev, newEvent]);
    return id;
  };

  const updateCalendarEvent = (id: string, event: Partial<CalendarEvent>) => {
    setCalendarEvents(prev => 
      prev.map(e => e.id === id ? { ...e, ...event } : e)
    );
  };

  const removeCalendarEvent = (id: string) => {
    setCalendarEvents(prev => prev.filter(e => e.id !== id));
  };

  const getCalendarEvent = (id: string) => {
    return calendarEvents.find(e => e.id === id);
  };

  return (
    <CalendarContext.Provider value={{
      calendarEvents,
      addCalendarEvent,
      updateCalendarEvent,
      removeCalendarEvent,
      getCalendarEvent
    }}>
      {children}
    </CalendarContext.Provider>
  );
};